import { apiClient } from './secureApiClient.js';
import { logger } from '../../utils/logger';
import { validatePlayerName } from '../../utils/validation/inputValidation';

/**
 * Creates a quick player without requiring full profile setup using secure API
 * @param {string} name - Player's name
 * @param {Array} players - Current players array
 * @param {Function} setPlayers - State setter for players
 * @returns {string|null} - Player ID if successful, null if failed
 */
export const handleQuickAddPlayer = async (name, players, setPlayers) => {
  try {
    // Validate player name
    const validation = validatePlayerName(name, players);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    const { name: validatedName } = validation.data;

    const playerData = {
      name: validatedName,
      isQuickAdd: true, // Flag to backend: don't link to admin's email/authUserId
      // Backend handles initials generation
    };

    logger.debug('Quick adding player via API:', playerData);
    logger.debug('Validation result:', validation);

    const result = await apiClient.createPlayer(playerData);
    const newPlayer = result.player || result;

    // Optimistic update: Add new player to local state immediately
    // Do NOT reload from server here to avoid eventual consistency issues (stale list)
    setPlayers(prevPlayers => {
      // Check if already exists to avoid duplicates
      if (prevPlayers.some(p => p.id === newPlayer.id)) {
        return prevPlayers;
      }
      return [...prevPlayers, newPlayer];
    });
    
    logger.debug('Player quick-added successfully via API');
    logger.debug('Reloaded players list for leaderboard refresh');
    logger.debug('Returning player ID:', newPlayer.id);
    return newPlayer.id;
  } catch (error) {
    logger.error('Error adding player:', error);
    throw error;
  }
};

/**
 * Creates a full user profile for authenticated users using secure API
 * @param {Event} e - Form submission event
 * @param {Object} user - Current authenticated user
 * @param {Array} players - Current players array
 * @param {Function} setPlayers - State setter for players
 * @param {Function} setUserProfile - State setter for user profile
 * @param {Function} setShowProfileModal - State setter for profile modal
 * @returns {Promise<void>}
 */
export const handleCreateProfile = async (
  e,
  user,
  players,
  setPlayers,
  setUserProfile,
  setShowProfileModal
) => {
  e.preventDefault();

  try {
    const formData = new FormData(e.target);
    const name = formData.get('name');

    // Validate player name
    const validation = validatePlayerName(name, players);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    const { name: validatedName } = validation.data;

    const playerData = {
      name: validatedName,
      // Backend handles email from token, no need to send it from frontend
    };

    logger.debug('Creating profile via API:', playerData);

    const newPlayer = await apiClient.createPlayer(playerData);

    // Set as admin if first player
    const updatedPlayer = {
      ...newPlayer,
      isAdmin: players.length === 0,
    };

    setPlayers([...players, updatedPlayer]);
    setUserProfile(updatedPlayer);
    setShowProfileModal(false);

    logger.debug('Profile created successfully via API');
  } catch (error) {
    logger.error('Error creating profile:', error);
    throw error;
  }
};

/**
 * Updates player profile (e.g., display name)
 * @param {string} playerId - Player ID to update
 * @param {string} newName - New display name
 * @param {Function} setUserProfile - State setter for user profile
 * @param {Function} loadPlayersData - Function to reload players data
 * @returns {Promise<Object>} - Updated player object
 */
export const updatePlayerProfile = async (playerId, newName, setUserProfile, loadPlayersData) => {
  try {
    logger.debug('Updating player profile via API:', { playerId, newName });

    const result = await apiClient.updatePlayer(playerId, { name: newName });
    const updatedPlayer = result.player || result;

    logger.debug('Player profile updated successfully:', updatedPlayer);

    // Update local state
    if (setUserProfile) {
      setUserProfile(updatedPlayer);
    }

    // Reload players to sync with server
    if (loadPlayersData) {
      await loadPlayersData();
    }

    return updatedPlayer;
  } catch (error) {
    logger.error('Error updating player profile:', error);
    throw error;
  }
};

/**
 * Syncs a whitelisted user with a player profile (Link or Create)
 * @param {Object} user - Auth0 user object
 * @param {string} assignedName - Name assigned in whitelist
 * @param {Array} players - Current list of players
 * @returns {Promise<{profile: Object|null, didUpdate: boolean}>}
 */
export const syncPlayerProfile = async (user, assignedName, players) => {
  // SECURITY: Validate inputs
  if (!user || !user.sub || !user.email) {
    logger.error('SECURITY ERROR: Invalid user object for sync', { user });
    return { profile: null, didUpdate: false };
  }

  if (!assignedName || typeof assignedName !== 'string') {
    logger.error('SECURITY ERROR: Invalid assignedName for sync', { assignedName });
    return { profile: null, didUpdate: false };
  }

  const userId = user.sub;
  const userEmail = user.email.toLowerCase();

  logger.info('SECURITY: Starting profile sync', {
    userId,
    userEmail,
    assignedName,
  });

  // 1. SECURITY PRIORITY: Check if already linked by authUserId or email using /players/me
  // The database email/authUserId is the source of truth for existing users
  try {
    const myProfile = await apiClient.getMyProfile();

    if (myProfile) {
      // SECURITY: User already exists in database - use existing profile
      logger.info('SECURITY: Existing profile found via /players/me', {
        profileId: myProfile.id,
        profileName: myProfile.name,
        profileEmail: myProfile.email,
        profileAuthUserId: myProfile.authUserId,
      });

      // Name in DB takes precedence over whitelist mapping
      return { profile: myProfile, didUpdate: false };
    }
  } catch (error) {
    logger.warn('SECURITY: /players/me endpoint failed, user not linked yet', {
      error: error.message,
      userId,
      userEmail,
    });
  }

  // 2. Not linked yet - First time login
  // Check for name match to claim existing unclaimed profile (Claiming)
  const nameMatch = players.find(p => {
    // SECURITY: Only match players without email/authUserId (unclaimed profiles)
    const isUnclaimed = !p.authUserId && !p.email;
    const nameMatches = p.name.toLowerCase() === assignedName.toLowerCase();
    return isUnclaimed && nameMatches;
  });

  try {
    if (nameMatch) {
      logger.info('SECURITY: Auto-linking user to existing unclaimed player', {
        userEmail,
        playerId: nameMatch.id,
        playerName: nameMatch.name,
      });

      // Link user to existing unclaimed player profile
      await apiClient.updatePlayer(nameMatch.id, {
        email: userEmail,
        authUserId: userId
      });

      return {
        profile: { ...nameMatch, email: userEmail, authUserId: userId },
        didUpdate: true
      };
    } else {
      logger.info('SECURITY: Auto-creating new player for whitelisted user', {
        userEmail,
        assignedName,
      });

      // Create new player with whitelist assigned name
      const result = await apiClient.createPlayer({
        name: assignedName,
      });

      const newPlayer = result.player || result;

      logger.info('SECURITY: New player created', {
        profileId: newPlayer.id,
        profileName: newPlayer.name,
        profileEmail: newPlayer.email,
      });

      return { profile: newPlayer, didUpdate: true };
    }
  } catch (error) {
    logger.error('SECURITY ERROR: Failed to sync player profile', {
      error: error.message,
      userId,
      userEmail,
      assignedName,
    });
    return { profile: null, didUpdate: false };
  }
};

/**
 * Loads all players using secure API
 * @returns {Promise<Array>} - Array of players
 */
export const loadPlayers = async () => {
  try {
    logger.debug('Loading players via secure API...');
    const players = await apiClient.getPlayers();

    logger.debug(
      'Players loaded successfully via API:',
      players?.length || 'undefined'
    );
    logger.debug('Players data:', players);
    return players;
  } catch (error) {
    logger.error('Error loading players via API:', error);
    return [];
  }
};
